import { Label, Input } from "@tellescope/design-system";
import { cn } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../../types";
import { useTranslation } from "react-i18next";
import { useId } from "react";
import { useForm } from "../../../use-form";
import { StateSelectField } from "./StateSelectField";
import { FieldDescription } from "../../FieldDescription";
import { combineZipCode } from "../../utils/get-required-address-keys";

export interface AddressFieldProps extends TellescopeFormFieldBaseProps<"Address"> {
  fullZIP?: boolean;
}

export const AddressField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  error,
  onBlur,
  required,
  options,
  fullZIP,
  autoFocus,
}: AddressFieldProps) => {
  const { t } = useTranslation("forms");
  const fieldId = useId();

  const addressFields = options?.addressFields;
  const hasFilter = addressFields && addressFields.length > 0;
  const validStates = options?.validStates ?? [];

  const showAddressLineOne = !hasFilter || addressFields.includes("addressLineOne");
  const showAddressLineTwo = !hasFilter || addressFields.includes("addressLineTwo");
  const showCity = !hasFilter || addressFields.includes("city");
  const showState = !hasFilter || addressFields.includes("state");
  const showZipCode = !hasFilter || addressFields.includes("zipCode");
  const fullZip = !!fullZIP;
  const requireIfVisible = (visible: boolean) => visible && !!required;

  const form = useForm({
    fields: {
      addressLineOne: {
        type: "text",
        initialValue: value?.addressLineOne,
        required: requireIfVisible(showAddressLineOne),
      },
      addressLineTwo: {
        type: "text",
        initialValue: value?.addressLineTwo,
      },
      city: {
        type: "text",
        initialValue: value?.city,
        required: requireIfVisible(showCity),
      },
      state: {
        type: "select",
        initialValue: value?.state,
        required: requireIfVisible(showState),
      },
      zipCode: {
        type: "text",
        initialValue: combineZipCode(value),
        required: requireIfVisible(showZipCode),
      },
    },
    validators: {
      zipCode: (val) => {
        if (!val) return undefined;
        if (fullZip) {
          return /^\d{5}-\d{4}$/.test(val) ? undefined : t("Please enter a valid zip code (e.g. 12345-6789)");
        }
        return /^\d{5}(-\d{4})?$/.test(val) ? undefined : t("Please enter a valid zip code");
      },
    },
    sideEffects: (newVal) => {
      onChange(newVal);
      return newVal;
    },
    validateOn: "blur",
  });

  const getFieldProps: typeof form.getFieldProps = (fieldName) => {
    const props = form.getFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  const getCustomFieldProps: typeof form.getCustomFieldProps = (fieldName) => {
    const props = form.getCustomFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  const showBottomRow = showCity || showState || showZipCode;

  const firstVisibleField = showAddressLineOne
    ? "addressLineOne"
    : showAddressLineTwo
    ? "addressLineTwo"
    : showCity
    ? "city"
    : showState
    ? "state"
    : "zipCode";
  const autoFocusFor = (fieldName: typeof firstVisibleField) => autoFocus && firstVisibleField === fieldName;

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div className="flex flex-col gap-2">
        {showAddressLineOne && (
          <div className="space-y-1">
            <Label htmlFor={`${fieldId}-addressLineOne`} required={required}>
              {t("Street Address")}
            </Label>
            <Input
              {...getFieldProps("addressLineOne")}
              id={`${fieldId}-addressLineOne`}
              placeholder={t("Address Line 1")}
              autoFocus={autoFocusFor("addressLineOne")}
            />
          </div>
        )}
        {showAddressLineTwo && (
          <div className="space-y-1">
            <Label htmlFor={`${fieldId}-addressLineTwo`}>{t("Apt, Suite, etc.")}</Label>
            <Input
              {...getFieldProps("addressLineTwo")}
              id={`${fieldId}-addressLineTwo`}
              placeholder={t("Address Line 2")}
              autoFocus={autoFocusFor("addressLineTwo")}
            />
          </div>
        )}
        {showBottomRow && (
          <div className="flex gap-2">
            {showCity && (
              <div className="flex-1 space-y-1">
                <Label htmlFor={`${fieldId}-city`} required={required}>
                  {t("City")}
                </Label>
                <Input
                  {...getFieldProps("city")}
                  id={`${fieldId}-city`}
                  placeholder={t("City")}
                  autoFocus={autoFocusFor("city")}
                />
              </div>
            )}
            {showState && (
              <div className="flex-1 space-y-1">
                <Label htmlFor={`${fieldId}-state`} required={required}>
                  {t("State")}
                </Label>
                <StateSelectField
                  {...getCustomFieldProps("state")}
                  id={`${fieldId}-state`}
                  validStates={validStates.length > 0 ? validStates : undefined}
                  placeholder={t("Select State")}
                  autoFocus={autoFocusFor("state")}
                />
              </div>
            )}
            {showZipCode && (
              <div className="flex-1 space-y-1">
                <Label htmlFor={`${fieldId}-zipCode`} required={required}>
                  {t("Zip Code")}
                </Label>
                <Input
                  {...getFieldProps("zipCode")}
                  id={`${fieldId}-zipCode`}
                  placeholder={fullZip ? t("Zip Code + 4 (12345-6789)") : t("Zip Code")}
                  autoFocus={autoFocusFor("zipCode")}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
